<template>
	<div class="theme">
		<form id="theme" @submit.prevent="$emit('submit')">
			<managing-section header="정보 입력">
				<managing-box title="제목">
					<text-counter
						ref="title"
						v-model="title"
						:max-length="50"
						placeholder="입력하세요."
						class="theme__text-counter"
					/>
				</managing-box>
				<managing-box title="노출기간" :horizontal-mode="true">
					<DateRange
						v-model="displayPeriod"
						ref="displayPeriod"
						:disabled="display === 1"
						class="theme__period"
					/>
					<RadioList v-model="display" :radio-list="displayList" />
				</managing-box>
				<managing-box title="태그">
					<text-counter
						ref="tags"
						v-model="tags"
						:max-length="1000"
						:use-comma="true"
						placeholder="콤마(,)로 구분"
						class="theme__text-counter"
					/>
				</managing-box>
				<managing-box title="순서">
					<input
						type="number"
						class="theme__order-counter"
						min="0"
						v-model="orderNumber"
					/>
				</managing-box>
			</managing-section>
		</form>
		<managing-section
			:header="`제품 검색 (총 ${contentTotal}건)`"
			:is-table="true"
			class="theme__search-section"
		>
			<template slot="infoSection">
				<div class="section__info__left">
					<horizontal-search
						:use-init-button="false"
						@search="searchProduct"
					>
						<SelectInput
							ref="searchType"
							:default-select-value="searchTypeList[0].value"
							:select-list="searchTypeList"
							:use-realtime-search="true"
							placeholder="입력하세요."
						/>
					</horizontal-search>
				</div>
				<div class="section__info__right">
					<div class="section__info__items">
						<selector v-model="order" :option-list="orderList" />
					</div>
					<div class="section__info__items">
						<selector
							v-model="limit"
							valueKey="id"
							:option-list="getLimitList()"
						/>
					</div>
				</div>
			</template>
			<product-table
				:product-list="parsedProductList"
				:use-select-button="true"
				@selecting="selecting"
				@product-updated="searchProduct"
			/>
			<pagination-box
				v-if="productList"
				slot="pagination"
				:total-item="contentTotal"
				:offset="offset"
				:limit="limit"
				class="theme__pagination"
				@to="paging"
			/>
		</managing-section>
		<managing-section
			:header="`제품 노출 순서 (총 ${selectedProductList.length}건)`"
			conts-height="125px"
			class="theme__order-section"
		>
			<div
				v-if="selectedProductList.length"
				class="theme__order-section__content"
			>
				<slick-list
					v-model="selectedProductList"
					axis="x"
					lockAxis="x"
					class="theme__order-section__content__list order-list"
					@input="listChange"
				>
					<slick-item
						v-for="(item, idx) in selectedProductList"
						:key="item.idProduct"
						:index="idx"
						class="order-list__item"
					>
						<img
							:src="item.imageUrl"
							class="order-list__item__img"
						/>
						<button
							type="button"
							class="btn-delete--only-icon order-list__item__delete-button"
							@click="deleteItem(item.idProduct)"
						></button>
					</slick-item>
				</slick-list>
			</div>
			<p v-else class="theme__order-section__empty">
				선택 된 제품이 없습니다.
			</p>
		</managing-section>
	</div>
</template>
<script>
	import moment from 'moment';
	import { mapGetters } from 'vuex';
	import ManagingSection from '@/components/layout/ManagingSection.vue';
	import ManagingBox from '@/components/layout/ManagingBox.vue';
	import TextCounter from '@/components/TextCounter.vue';
	import DateRange from '@/components/DateRange';
	import RadioList from '@/components/RadioList';
	import HorizontalSearch from '@/components/HorizontalSearch.vue';
	import SelectInput from '@/components/SelectInput.vue';
	import Selector from '@/components/Selector.vue';
	import { SlickList, SlickItem } from 'vue-slicksort';
	import PaginationBox from '@/components/PaginationBox.vue';
	import ProductTable from '@/components/ProductTable.vue';

	export default {
		name: 'ThemeController',
		components: {
			ManagingSection,
			ManagingBox,
			TextCounter,
			DateRange,
			RadioList,
			HorizontalSearch,
			SelectInput,
			Selector,
			SlickList,
			SlickItem,
			PaginationBox,
			ProductTable,
		},
		props: {
			themeData: {
				type: Object,
				default: null,
			},
		},
		data() {
			return {
				originData: null,
				title: '',
				displayPeriod: { from: '', to: '' },
				displayList: [
					{ id: 0, value: '기간설정' },
					{ id: 1, value: '비노출' },
				],
				display: 0,
				tags: '',
				searchTypeList: [
					{ value: 'idProduct', name: '제품번호' },
					{ value: 'productTitle', name: '제품명' },
					{ value: 'searchValue', name: '브랜드 + 제품명' },
				],
				orderList: [
					{ value: 1, name: '최신순' },
					{ value: 2, name: '평점 높은 순' },
					{ value: 3, name: '리뷰 많은 순' },
				],
				order: 1,
				limit: 10,
				offset: 0,
				contentTotal: 0,
				productList: null,
				selectedProductList: [],
				orderNumber: null,
			};
		},
		computed: {
			...mapGetters(['getLimitList']),
			parsedProductList() {
				const selectedList = this.selectedProductList;
				const list = this.productList;
				return !selectedList.length || !list || !list.length
					? list
					: list.map(item => {
							if (
								selectedList.some(
									selectedItem =>
										selectedItem.idProduct ===
										item.idProduct
								)
							)
								item.selected = true;
							return item;
						});
			},
		},
		watch: {
			order() {
				if (!this.productList || !this.productList.length) return;
				this.offset = 0;
				this.searchProduct();
			},
			limit() {
				if (!this.productList || !this.productList.length) return;
				this.offset = 0;
				this.searchProduct();
			},
		},
		methods: {
			init(data) {
				this.originData = data;
				this.$refs.title.textValue = this.title = data.title || '';
				if (!data.startOn && !data.endOn) {
					this.display = 1;
				} else {
					const from = data.startOn
						? moment(data.startOn, 'x').format('YYYY-MM-DDTHH:mm')
						: '';
					const to = data.endOn
						? moment(data.endOn, 'x').format('YYYY-MM-DDTHH:mm')
						: '';
					this.displayPeriod = { from, to };
					this.originData.startOn = from;
					this.originData.endOn = to;
				}
				if (data.tags.length) {
					this.tags = data.tags.join(',');
					this.$refs.tags.textValue = data.tags.join(',');
				}
				if (data.products.length) {
					this.selectedProductList = data.products.map(item => {
						return {
							idProduct: item.idProduct,
							imageUrl: item.imageUrl,
						};
					});
				}
				if (data.sort) {
					this.orderNumber = data.sort;
				}
			},
			async searchProduct() {
				const params = {
					offset: this.offset,
					limit: this.limit,
					order: this.order,
				};
				params[this.$refs.searchType.selectModel] =
					this.$refs.searchType.inputModel;
				const { data } = await this.$axiosAdmin2.get(
					'/api/products/search',
					{ params }
				);
				this.contentTotal = data.contentTotal;
				this.productList = data.data.map(item => {
					let category;
					if (item.firstCategoryText) {
						category = [item.firstCategoryText];
						if (item.secondCategoryText) {
							category.push(item.secondCategoryText);
							if (item.thirdCategoryText)
								category.push(item.thirdCategoryText);
						}
					}
					item.category = category ? category.join(' > ') : '';
					item.volume = `${item.volume || '-'} / ${item.price || '-'}`;
					return item;
				});
			},
			paging({ offset }) {
				this.offset = offset;
				this.searchProduct();
			},
			selecting(item) {
				if (item.selected) return;
				this.selectedProductList.push({
					idProduct: item.idProduct,
					imageUrl: item.productImg,
				});
			},
			listChange(newList) {
				this.selectedProductList = newList;
			},
			deleteItem(id) {
				this.selectedProductList.splice(
					this.selectedProductList.findIndex(
						item => item.idProduct === id
					),
					1
				);
				if (!this.productList) return;
				const targetIdx = this.productList.findIndex(
					item => item.idProduct === id
				);
				if (targetIdx > -1) {
					this.productList[targetIdx].selected = false;
				}
			},
			validate() {
				if (!this.title) {
					alert('제목을 입력하세요.');
					this.$refs.title.$refs.input.focus();
					return false;
				} else if (!this.display && !this.displayPeriod.from) {
					alert('노출기간 시작시간을 입력하세요.');
					this.$refs.displayPeriod.focus();
					return false;
				} else if (!this.display && !this.displayPeriod.to) {
					alert('노출기간 종료시간을 입력하세요.');
					this.$refs.displayPeriod.focus('to');
					return false;
				} else if (!this.display && this.displayPeriod) {
					const start = moment(this.displayPeriod.from).valueOf();
					const end = moment(this.displayPeriod.to).valueOf();
					if (start > end) {
						alert('노출기간 종료일이 시작일보다 앞서있습니다');
						return false;
					}
				} else if (!this.selectedProductList.length) {
					alert('제품을 1개 이상 선택해주세요.');
					return false;
				}
				return true;
			},
			getParams() {
				if (!this.validate()) return false;
				const params = {
					title: this.title,
					products: this.selectedProductList.map((item, idx) => ({
						sort: idx + 1,
						idProduct: item.idProduct,
					})),
				};
				if (!this.display) {
					params.startOn = this.displayPeriod.from
						? moment(
								this.displayPeriod.from,
								'YYYY-MM-DDTHH:mm'
							).format('x')
						: null;
					params.endOn = this.displayPeriod.to
						? moment(
								this.displayPeriod.to,
								'YYYY-MM-DDTHH:mm'
							).format('x')
						: null;
				}
				if (this.tags) {
					params.tags = this.tags.split(',');
				}
				params.sort = this.orderNumber
					? Number(this.orderNumber)
					: null;
				return params;
			},
			async register() {
				const params = this.getParams();
				if (!params) return false;
				await this.$axiosAdmin2.post('/v3/api/theme', params);
				return true;
			},
			async modify() {
				const params = this.getParams();
				if (!params) return false;
				await this.$axiosAdmin2.put(
					`/v3/api/theme/${this.$route.params.idTheme}`,
					params
				);
				return true;
			},
			hasChanges() {
				const origin = this.originData;
				const selected = this.selectedProductList;
				return !(
					origin.title === this.title &&
					origin.startOn === this.displayPeriod.from &&
					origin.endOn === this.displayPeriod.to &&
					origin.tags.join(',') === this.tags &&
					origin.products.length === selected.length &&
					!origin.products.some(
						(item, idx) =>
							item.idProduct !== selected[idx].idProduct
					)
				);
			},
			preview() {
				const productIds = this.selectedProductList.length
					? this.selectedProductList.map(item => item.idProduct)
					: null;
				if (!productIds) {
					alert('선택된 제품이 없습니다.');
					return;
				}
				this.$store.commit('modal/toggleModal', {
					id: 'ThemePreview',
					params: { productIds, title: this.title },
				});
			},
		},
	};
</script>
<style lang="scss" scoped>
	.theme__text-counter {
		width: 100%;
	}

	.theme__period {
		margin-right: 16px;
	}

	.theme__pagination {
		margin-top: 14px;
	}

	.theme__search-section ::v-deep {
		.section__table-wrap {
			overflow-y: hidden;
		}

		.product-table {
			margin-bottom: -1px;
		}
	}

	.theme__order-section ::v-deep .section__conts-wrap {
		border-top: none;
	}

	.theme__order-section__content {
		position: relative;
		height: 125px;
		background: white;
		border: solid 1px #dfe6ee;
	}

	.order-list {
		position: absolute;
		white-space: nowrap;
		font-size: 0;
		overflow: auto;
		width: 100%;
		padding: 16px;
	}

	.order-list__item {
		display: inline-block;
		position: relative;
		width: 91px;
		height: 91px;
		margin-left: 16px;
		border: solid 1px #dfe6ee;

		&:first-child {
			margin-left: 0;
		}
	}

	.order-list__item__img {
		display: block;
		width: 89px;
		height: 89px;
	}

	.order-list__item__delete-button {
		position: absolute;
		top: 0;
		right: 0;
		width: 20px;
		height: 20px;
		padding: 0;
		border-radius: 0;
		line-height: 18px;
	}

	.theme__order-section__empty {
		width: 100%;
		height: 100%;
		text-align: center;
		background: white;
		line-height: 123px;
		border: solid 1px #dfe6ee;
	}

	.theme__order-counter {
		width: 70px !important;
	}
</style>
